import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { V, Haptic } from '../../utils/theme';
import { LS } from '../../utils/storage';
import { Icons } from '../../components/Icons';
import { Card, Btn, Field, Sheet, Chip, Stat, Progress, Skeleton, SkeletonCard, MsgBannerCtrl, SuccessToastCtrl, ConfirmCtrl } from '../../components/ui';
import { today, ago, fmtShort, fmtFull, uid, friendDisplayName, convW, wUnit, calc1RM, calcStreak } from '../../utils/helpers';
import { SocialAPI, SYNC_URL } from '../../utils/sync';
import { ShareCard } from '../../utils/share';
import { BADGE_DEFS, calcEarnedBadges } from '../../data/badges';
import { useLayout } from '../../utils/responsive';
import { IRON_RANKS } from '../../data/ranks';
import { HelpBtn, compressImage } from '../features';
import { IronScoreCard, RankBadge, DailyMissionsCard } from '../gamification';
import { useStreak, calcReadiness } from '../../components/dialogs';
import { SectionGrid } from '../hubs';
import { MS_PER_MINUTE, MS_PER_HOUR, MS_PER_DAY, ONLINE_THRESHOLD_MS } from '../../utils/constants';
import { apiPost } from '../../utils/api';

export function SocialTab({s,d,unreadMsgCount=0}){
  const { isDesktop } = useLayout();
  // Quick stats for display
  const email=s.profile?.email;
  const streak=useStreak(s.workouts);
  const weekW=(s.workouts||[]).filter(w=>w.date>=ago(7)).length;
  const [notifCount,setNotifCount]=useState(0);
  useEffect(()=>{if(email)SocialAPI.getNotifications(email).then(r=>{if(r)setNotifCount(r.unread||0);});},[email]);

  return(
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div>
          <div style={{fontSize:22,fontWeight:800,color:V.text}}>Community</div>
          <div style={{fontSize:12,color:V.text3}}>Connect, compete, stay accountable</div>
        </div>
        {notifCount>0&&(
          <button onClick={()=>d({type:"TAB",tab:"social_notifications"})} style={{padding:"6px 12px",borderRadius:8,
            background:`${V.danger}15`,border:`1px solid ${V.danger}30`,cursor:"pointer",
            fontSize:11,fontWeight:700,color:V.danger,fontFamily:V.font}}>
            🔔 {notifCount}
          </button>
        )}
      </div>

      {/* Quick stats bar */}
      <div style={{display:"flex",gap:8}}>
        {[{l:"Streak",v:`${streak}d`,c:streak>=7?V.accent:V.text3},{l:"This Week",v:`${weekW}`,c:weekW>=3?V.accent:V.text3},
          {l:"Workouts",v:`${(s.workouts||[]).length}`,c:V.purple}].map(st=>(
          <div key={st.l} style={{flex:1,padding:"8px",borderRadius:8,background:V.card,
            border:`1px solid ${V.cardBorder}`,textAlign:"center"}}>
            <div style={{fontSize:16,fontWeight:800,color:st.c,fontFamily:V.mono}}>{st.v}</div>
            <div style={{fontSize:11,color:V.text3,fontWeight:600}}>{st.l}</div>
          </div>
        ))}
      </div>

      {/* Iron Score compact rank */}
      <button onClick={()=>d({type:"TAB",tab:"social_ironscore"})} style={{display:"block",width:"100%",
        background:"none",border:"none",cursor:"pointer",padding:0,textAlign:"left"}}>
        <IronScoreCard s={s}/>
      </button>

      <SectionGrid d={d} title="Your Activity" items={[
        {id:"social_feed",icon:Icons.activity,label:"Feed",desc:"Today's stats & friend activity",color:"#ec4899",flag:"feed"},
        {id:"social_profile",icon:Icons.target,label:"Profile",desc:"QR code, friend code & privacy",color:"#8b5cf6"},
        {id:"social_notifications",icon:Icons.zap,label:"Notifications",desc:notifCount>0?`${notifCount} unread`:"All caught up",color:notifCount>0?V.danger:"#64748b"},
      ]}/>

      <SectionGrid d={d} title="Connections" items={[
        {id:"social_messages",icon:Icons.activity,label:"Messages",desc:unreadMsgCount>0?`${unreadMsgCount} unread`:"iMessage-style DMs",color:"#22c55e",badge:unreadMsgCount,flag:"messages_social"},
        {id:"social_friends",icon:Icons.target,label:"Friends",desc:"Add, compare & manage",color:"#06b6d4",flag:"friends"},
        {id:"social_groups",icon:Icons.target,label:"Groups",desc:"Crews, leaderboards & stats",color:"#f59e0b",flag:"groups"},
        {id:"social_compare",icon:Icons.chart,label:"You vs Best",desc:"Personal records race",color:"#a78bfa"},
      ]}/>

      <SectionGrid d={d} title="Compete" items={[
        {id:"social_ironscore",icon:Icons.trophy,label:"Iron Score",desc:`${(()=>{const xp2=LS.get("ft-ironscore")||0;const l=[...IRON_RANKS].reverse().find(lv=>xp2>=lv.xpNeeded)||IRON_RANKS[0];return l.icon+" "+l.name+" · "+xp2.toLocaleString()+" XP";})()}`,color:"#f59e0b"},
        {id:"social_duels",icon:Icons.zap,label:"1v1 Duels",desc:`${(LS.get("ft-duels")||[]).filter(d=>d.status==="active").length} active`,color:"#f43f5e",flag:"duels"},
        {id:"social_wars",icon:Icons.trophy,label:"Weekly Wars",desc:"New battle every Monday",color:"#ec4899"},
        {id:"social_rivals",icon:Icons.target,label:"Rivals",desc:`${(LS.get("ft-rivals")||[]).length} tracked`,color:"#f97316"},
        {id:"social_challenges",icon:Icons.trophy,label:"Challenges",desc:"Tiers, badges & weekly",color:"#f43f5e"},
        {id:"social_badges",icon:Icons.target,label:"Badges",desc:"Your achievements",color:"#f97316"},
        {id:"social_leaderboard",icon:Icons.chart,label:"Leaderboard",desc:"Group rankings",color:"#10b981"},
        {id:"social_league",icon:Icons.trophy,label:"League",desc:"Weekly XP cohorts",color:"#eab308",flag:"league"},
      ]}/>
    </div>
  );
}

// ─── Social Sub-Page: Feed ───
